import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Home, Menu, Phone, QrCode, X } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import ArabAliLogo from '../ArabAliLogo';

const Header: React.FC = () => {
  const location = useLocation();
  const { restaurant, settings } = useRestaurant();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/menu', label: 'Menu', icon: BookOpen },
    { path: '/qr-code', label: 'QR', icon: QrCode },
    { path: '/contact', label: 'Contact', icon: Phone },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'border-b border-amber-400/20 bg-[#180a08]/95 shadow-[0_18px_45px_rgba(0,0,0,0.45)] backdrop-blur-xl'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <ArabAliLogo compact logoImage={settings.logoImage} name={restaurant.name} />
            <div className="hidden min-w-0 sm:block">
              <div className="truncate arab-gold-text text-sm font-semibold uppercase tracking-[0.26em]">
                {restaurant.name}
              </div>
              <div className="truncate text-[11px] uppercase tracking-[0.4em] text-amber-100/60">
                {restaurant.location}
              </div>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`rounded-full px-4 py-2 text-sm font-medium uppercase tracking-[0.18em] transition-all duration-200 ${
                  isActive(path)
                    ? 'text-[#2a1209] shadow-[0_10px_25px_rgba(214,132,34,0.35)]'
                    : 'text-amber-50 hover:bg-white/10'
                }`}
                style={
                  isActive(path)
                    ? {
                        background: `linear-gradient(135deg, ${settings.brandColors.primary}, ${settings.brandColors.accent})`,
                      }
                    : undefined
                }
              >
                <Icon className="mr-2 inline h-4 w-4" />
                {label}
              </Link>
            ))}
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-400/20 bg-white/10 transition-colors hover:bg-white/20 md:hidden"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 text-white" />
            ) : (
              <Menu className="h-5 w-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm md:hidden">
          <button
            type="button"
            aria-label="Close menu backdrop"
            className="absolute inset-0 h-full w-full"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm border-l border-amber-300/10 bg-[#140907]/96 p-5 shadow-[0_20px_70px_rgba(0,0,0,0.45)]">
            <div className="mb-6 flex items-center justify-between">
              <ArabAliLogo compact logoImage={settings.logoImage} name={restaurant.name} />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-400/20 bg-white/10 transition-colors hover:bg-white/20"
                aria-label="Close menu"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            <div className="mb-5 rounded-[24px] border border-white/10 bg-white/5 p-4">
              <div className="arab-gold-text text-sm font-semibold uppercase tracking-[0.28em]">{restaurant.name}</div>
              <div className="mt-2 text-sm text-white/60">Browse the menu, scan the QR code, or contact the restaurant.</div>
            </div>

            <nav className="space-y-3">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center gap-3 rounded-2xl px-4 py-4 font-medium uppercase tracking-[0.12em] transition-all duration-200 ${
                    isActive(path)
                      ? 'text-[#2a1209]'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  style={
                    isActive(path)
                      ? {
                          background: `linear-gradient(135deg, ${settings.brandColors.primary}, ${settings.brandColors.accent})`,
                        }
                      : undefined
                  }
                >
                  <Icon className="h-5 w-5" />
                  <div className="font-bold">{label}</div>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
